import React, { useEffect, useMemo, useState } from 'react';
import { ChannelSort } from 'stream-chat';
import { Channel, ChannelList, Chat, Streami18n } from 'stream-chat-react';

import 'stream-chat-react/dist/css/v2/index.css';
import './index.css';

import {
  ChatAvatar,
  CreateChannel,
  CustomMessage,
  MessagingChannelList,
  MessagingChannelListHeader,
  MessagingInput,
  MessagingThreadHeader,
} from './components';

import { ChannelInner } from './components/ChannelInner/ChannelInner';

import { useStore } from '@stores';
import { observer } from 'mobx-react';

const sort: ChannelSort = {
  last_message_at: -1,
  updated_at: -1,
};

export const GiphyContext = React.createContext(
  {} as { giphyState: boolean; setGiphyState: React.Dispatch<React.SetStateAction<boolean>> }
);

interface IProps {
  filter?: string;
  channelID?: string;
  hideChannelList?: boolean;
  chatClassName?: string;
}

export const SharedChat: React.FC<IProps> = observer(
  ({ channelID, filter = '', hideChannelList = false, chatClassName = '' }) => {
    const { sessionStore } = useStore();
    const { currentLogin, chatClient } = sessionStore;
    const [giphyState, setGiphyState] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [isMobileNavVisible, setMobileNav] = useState(!hideChannelList);
    const [theme, setTheme] = useState('light');

    const i18nInstance = useMemo(
      () =>
        new Streami18n({
          language: 'en',
          dayjsLocaleConfigForLanguage: {
            formats: {
              L: 'DD/MM/YYYY',
              LL: 'D MMMM YYYY',
              LLL: 'D MMMM YYYY HH:mm',
              LLLL: 'dddd, D MMMM YYYY HH:mm',
              LT: 'HH:mm',
              LTS: 'HH:mm:ss',
            },
          },
        }),
      []
    );

    const chatTenantId = currentLogin?.tenant?.tenancyName + currentLogin?.tenant?.id;
    const members = (filter && { $eq: [chatTenantId, filter] }) || { $in: [chatTenantId] };
    const ID = channelID && { $eq: channelID };
    const type = 'messaging';
    const filters = { type, members, ID };
    const options = { state: true, watch: true, presence: true, limit: 8 };

    useEffect(() => {
      const handleThemeChange = ({ data, origin }: { data: string; origin: string }) => {
        // handle events only from trusted origin
        if (origin === '') {
          if (data === 'light' || data === 'dark') {
            setTheme(data);
          }
        }
      };

      window.addEventListener('message', handleThemeChange);
      return () => window.removeEventListener('message', handleThemeChange);
    }, []);

    useEffect(() => {
      const mobileChannelList = document.querySelector('#mobile-channel-list');
      if (isMobileNavVisible && mobileChannelList) {
        mobileChannelList.classList.add('show');
        document.body.style.overflow = 'auto';
      } else if (!isMobileNavVisible && mobileChannelList) {
        mobileChannelList.classList.remove('show');
        document.body.style.overflow = 'auto';
      }
    }, [isMobileNavVisible]);

    useEffect(() => {
      /*
       * Get the actual rendered window height to set the container size properly.
       * In some browsers (like Safari) the nav bar can override the app.
       */
      const setAppHeight = () => {
        const doc = document.documentElement;
        doc.style.setProperty('--app-height', `${window.innerHeight}px`);
      };

      setAppHeight();

      window.addEventListener('resize', setAppHeight);
      return () => window.removeEventListener('resize', setAppHeight);
    }, []);

    const toggleMobile = () => setMobileNav(!isMobileNavVisible);

    const handleSidebarClick = (e: React.MouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;
      if (target.closest('.str-chat__channel-preview')) {
        toggleMobile();
      }
    };

    const giphyContextValue = { giphyState, setGiphyState };

    if (!chatClient) return null;

    return (
      <div className={chatClassName || ''}>
        <Chat client={chatClient} i18nInstance={i18nInstance} theme={`str-chat__theme-${theme}`}>
          <div
            style={{
              visibility: hideChannelList ? 'hidden' : 'visible',
              ...(hideChannelList && { display: 'none' }),
            }}
            className={!hideChannelList ? 'messaging__sidebar' : ''}
            id="mobile-channel-list"
            onClick={handleSidebarClick}
          >
            <MessagingChannelListHeader
              isCreating={isCreating}
              onCreateChannel={() => setIsCreating(!isCreating)}
              theme={theme}
            />
            <ChannelList
              filters={filters}
              sort={sort}
              options={options}
              Avatar={ChatAvatar}
              List={(props) => <MessagingChannelList {...props} channelID={channelID} />}
            />
          </div>

          <div>
            <Channel
              Avatar={ChatAvatar}
              Input={MessagingInput}
              Message={CustomMessage}
              ThreadHeader={MessagingThreadHeader}
              TypingIndicator={() => null}
            >
              {isCreating && (
                <CreateChannel
                  filter={chatTenantId + filter}
                  toggleMobile={toggleMobile}
                  onClose={() => setIsCreating(false)}
                />
              )}
              <GiphyContext.Provider value={giphyContextValue}>
                <ChannelInner theme={theme} toggleMobile={toggleMobile} />
              </GiphyContext.Provider>
            </Channel>
          </div>
        </Chat>
      </div>
    );
  }
);
